/**
 * Imports
 * @ignore
 * @internal
 */
import { I18n } from '@aws-amplify/core'
import { useTheme, makeStyles } from '@material-ui/core/styles'
import { LOAD } from 'actions/dashboard'
import ChartWrapper from 'components/chart/ChartWrapper'
import { labelX, labelY } from 'components/chart/constants'
import GraphTooltip from 'components/graph-tooltip/'
import RenderTick from 'components/page/reporting/render-tick'
import dayjs from 'dayjs'
import { indexer } from 'helpers'
import { ReactElement } from 'react'
import { useSelector } from 'react-redux'
import { Bar, BarChart, CartesianGrid, Label, Tooltip, XAxis, YAxis, LabelList } from 'recharts'
import { createSelector } from 'reselect'
import { State } from 'types/state'
import dayOfYear from 'dayjs/plugin/dayOfYear'

//dayjs.extend(dayOfYear)

/**
 * Localization
 * @ignore
 * @internal
 */
const I18N_NS = 'page.reporting.rainfall.rainfallDaily'
I18n.putVocabularies({
  en: {
    [`${I18N_NS}.title`]: 'Daily Rainfall',
    [`${I18N_NS}.highestRainfallTitle`]: 'Highest Rainfall',
    [`${I18N_NS}.lowestRainfallTitle`]: 'Lowest Rainfall',
    [`${I18N_NS}.xAxis`]: 'Weekday',
    [`${I18N_NS}.yAxis`]: 'Rainfall (mm)',
    [`${I18N_NS}.minLegend`]: 'Rainfall (mm)',
    [`${I18N_NS}.value.graphTooltip`]: 'Total Rainfall',
    [`${I18N_NS}.valueMax.graphTooltip`]: 'Rainfall Max'
  }
})

const weeklyRainfall = createSelector(
  (state: State) => state.dashboard?.dailyRainfall?.daily || [],
  (daily) => {
    const days: { date: string; dateLabel: string; weekDay: number }[] = []
    for (let i = 7; i >= 1; i--) {
      const d = dayjs().subtract(i, 'day')
      days.push({
        date: d.format('YYYY-MM-DD'),
        dateLabel: d.format('ddd D'),
        weekDay: d.day()
      })
    }


    const used = new Set<number>()
    const data = days.map(({ date, dateLabel, weekDay }) => {
      const entry = daily.find((d) => d.weekDay === weekDay && !used.has(weekDay))
      if (entry) used.add(weekDay)
      return {
        date,
        dateLabel,
        weekDay,
        value: entry?.sum ?? null
      }
    })

    return { data }
  }
)

/** 
 * Formal date
 * @param d Dayjs date
 * @param full Display as full date
 * @returns Formatted Date
*/

const formatDateLabel = (dateLabel: string, full = false) => {
  if (!dateLabel) return ''
  if (full) {
    // e.g. "Tuesday 11"
    const d = dayjs(dateLabel, 'ddd D')
    return d.isValid() ? d.format('dddd, MMM D') : dateLabel
  }
  return dateLabel // e.g. "Tue 11"
}

/** 
 * Mean and max rainfall
 * 
 * @param props Element Properties
 * @param props.loading Loading state
 * @params props.data Data
 * @returns Peak day react element
*/

const useStyles = makeStyles(() => ({
  transparentChart: {
    '& .MuiPaper-root': {
      backgroundColor: 'rgba(60, 60, 60, 0.4)',
      background: 'linear-gradient(135deg, rgba(200, 200, 200, 0.15) 0%, rgba(100, 100, 100, 0.05) 100%)',
      backdropFilter: 'blur(16px)',
      boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.5)',
      borderRadius: '24px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    },
    '& .MuiTypography-root': {
      display: 'none', // hide Title/Subtitle text to match mock
    }
  }
}))

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const { dateLabel, value } = payload[0].payload
    return (
      <div style={{
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '12px',
        padding: '12px 16px',
        color: '#fff',
        boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
      }}>
        <p style={{ margin: '0 0 6px 0', fontSize: 13, fontWeight: 'bold' }}>{formatDateLabel(dateLabel, true)}</p>
        <p style={{ margin: 0, fontSize: 12, color: '#E0E0E0' }}>
          {I18n.get(`${I18N_NS}.value.graphTooltip`)}: <strong style={{ color: '#FFF' }}>{value != null ? Number(value).toFixed(1) : '—'}mm</strong>
        </p>
      </div>
    );
  }
  return null;
};

const CustomLabel = (props: any) => {
  const { x, y, width, height, value } = props;
  const cx = x + width / 2;
  const isShortValue = height < 30;
  const cy = isShortValue ? y - 20 : y + height / 2;

  if (value == null) return null;
  const strValue = `${Number(value).toFixed(0)}mm`;

  return (
    <g transform={`translate(${cx}, ${cy})`}>
      <text
        x={0}
        y={0}
        dy={4}
        fill={isShortValue ? '#DDDDDD' : '#777777'}
        fontSize={12}
        fontWeight="bold"
        textAnchor="middle"
        transform="rotate(-90)"
      >
        {strValue}
      </text>
    </g>
  );
};

const DailyRainfall = ({ loading, data }) => {
  const theme = useTheme()
  const classes = useStyles()

  return (
    <div className={classes.transparentChart} style={{ position: 'relative' }}>
      <div style={{
        position: 'absolute',
        top: 16,
        left: 24,
        color: '#FFFFFF',
        fontWeight: 'bold',
        fontSize: '14px',
        zIndex: 10,
        pointerEvents: 'none'
      }}>
        {I18n.get(`${I18N_NS}.title`)}
      </div>

      <ChartWrapper
        loading={loading}
        noData={!data && !loading}
        height={220}
        css={{ p: 2, subtitleHeight: 0, contentHeight: 160 }}
      >
        <BarChart data={data} margin={{ top: 40, right: 30, left: 20, bottom: 0 }}>
          <XAxis
            dataKey="dateLabel"
            tickLine={false}
            dy={16}
            height={40}
            tick={{ fill: '#FFFFFF', fontSize: 12, fontWeight: 'bold' }}
            axisLine={false}
          >
          </XAxis>
          <YAxis
            orientation="right"
            tickLine={false}
            dx={10}
            width={40}
            tick={{ fill: 'rgba(255, 255, 255, 0.8)', fontSize: 12, fontWeight: 'bold' }}
            axisLine={false}
          />
          <CartesianGrid strokeDasharray="4 4" stroke="rgba(255,255,255,0.25)" vertical={false} />

          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={<CustomTooltip />}
          />
          <Bar
            maxBarSize={20}
            dataKey="value"
            fill="#FFFFFF"
            radius={20}>
            <LabelList dataKey="value" content={<CustomLabel />} />
          </Bar>
        </BarChart>
      </ChartWrapper>
    </div>
  )
}

export const ConnectedDailyRainfall = (): ReactElement => {
  const loading = useSelector((state: any) => state.loader[LOAD.GET_DAILY_RAINFALL])
  const { data } = useSelector(weeklyRainfall)

  return <DailyRainfall
    loading={loading}
    data={data} />
}

export default (): ReactElement => {
  return (
    <ConnectedDailyRainfall />
  )
}